import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  DEFAULT_OG_IMAGE_PATH,
  SITE_NAME,
  normalizePathname,
  organizationJsonLd,
  resolveOrigin,
  resolveSeo,
} from '../config/seo';

function upsertMeta(selector: string, create: () => HTMLMetaElement, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = create();
    el.setAttribute('data-seo', '1');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLinkCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"][data-seo="1"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    el.setAttribute('data-seo', '1');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id: string, json: object) {
  const existing = document.getElementById(id);
  const text = JSON.stringify(json);
  if (existing && existing.textContent === text) return;
  existing?.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.setAttribute('data-seo', '1');
  script.textContent = text;
  document.head.appendChild(script);
}

/**
 * Per-route document title, meta description, canonical, Open Graph, Twitter, robots, JSON-LD.
 */
export default function SeoHead() {
  const { pathname } = useLocation();
  const path = useMemo(() => normalizePathname(pathname), [pathname]);
  const seo = useMemo(() => resolveSeo(pathname), [pathname]);

  // DRY: single source of truth for canonical origin (KAN-SEO-BLOGPOSTING-001 #33).
  const origin = useMemo(() => resolveOrigin(), []);

  useEffect(() => {
    document.title = seo.title;

    upsertMeta(
      'meta[name="description"][data-seo="1"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('name', 'description');
        return m;
      },
      seo.description,
    );

    const canonicalUrl = `${origin}${path === '/' ? '/' : path}`;
    upsertLinkCanonical(canonicalUrl);

    upsertMeta(
      'meta[property="og:title"][data-seo="1"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:title');
        return m;
      },
      seo.title,
    );
    upsertMeta(
      'meta[property="og:description"][data-seo="1"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:description');
        return m;
      },
      seo.description,
    );
    upsertMeta(
      'meta[property="og:url"][data-seo="1"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:url');
        return m;
      },
      canonicalUrl,
    );
    upsertMeta(
      'meta[property="og:type"][data-seo="1"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:type');
        return m;
      },
      'website',
    );
    upsertMeta(
      'meta[property="og:site_name"][data-seo="1"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:site_name');
        return m;
      },
      SITE_NAME,
    );
    const ogImage = `${origin}${DEFAULT_OG_IMAGE_PATH}`;
    upsertMeta(
      'meta[property="og:image"][data-seo="1"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:image');
        return m;
      },
      ogImage,
    );

    upsertMeta(
      'meta[name="twitter:card"][data-seo="1"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('name', 'twitter:card');
        return m;
      },
      'summary_large_image',
    );
    upsertMeta(
      'meta[name="twitter:title"][data-seo="1"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('name', 'twitter:title');
        return m;
      },
      seo.title,
    );
    upsertMeta(
      'meta[name="twitter:description"][data-seo="1"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('name', 'twitter:description');
        return m;
      },
      seo.description,
    );
    upsertMeta(
      'meta[name="twitter:image"][data-seo="1"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('name', 'twitter:image');
        return m;
      },
      ogImage,
    );

    const robotsContent = seo.noindex ? 'noindex, nofollow' : 'index, follow';
    upsertMeta(
      'meta[name="robots"][data-seo="1"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('name', 'robots');
        return m;
      },
      robotsContent,
    );

    if (!seo.noindex) {
      upsertJsonLd('seo-organization-website', organizationJsonLd(origin));
    } else {
      document.getElementById('seo-organization-website')?.remove();
    }
  }, [origin, path, seo.description, seo.noindex, seo.title]);

  return null;
}
